import * as React from 'react';
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    AnnualPolicyPremiumTypeEnum,
    FalconPaymentResponse,
    ICacheManager,
    ICustomerPortalService,
    ILoggerService,
    MissedPayment,
    productHelper,
    resourcesHelper,
    Types,
    VerifyNewCardResponse
} from "@falcon/services";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import FalconFooter from "../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {Navigate} from "react-router-dom";
import {getAmountToDisplay, getSessionStorage} from "../../../../CommonFunctions";

interface IParams {
    policyNumber: string
    paymentReference: string
    [key: string]: string | undefined
}

interface IState {
    paymentSuccess: boolean;
    paymentCancelled: boolean;
    cardVerified: boolean;
    product: string;
    policyNumber: string;
    paymentAmountReceived: boolean;
    premiumType?: string;
    dataLoaded: boolean;
    isChargeBack: boolean;
    reuseCard: boolean;
    failureRedirectUrl: string;
    cardNumberObfuscated: string;
}

@authorize
export class PaymentProcessingPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    @lazyInject(Types.LoggerService)
    protected apmRumLoggerService: ILoggerService;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            paymentSuccess: false,
            paymentCancelled: false,
            cardVerified: false,
            product: getSessionStorage("missedPaymentProduct"),
            policyNumber: getSessionStorage("missedPaymentPolicyNumber"),
            paymentAmountReceived: false,
            dataLoaded: false,
            isChargeBack: getSessionStorage("isChargeBack"),
            reuseCard: getSessionStorage("reuseCard"),
            failureRedirectUrl: "",
            cardNumberObfuscated: ""
        };

        this.verifyNewCardMissedPayment = this.verifyNewCardMissedPayment.bind(this);
        this.verificationNeeded = this.verificationNeeded.bind(this);
        this.existingCardMissedPayment = this.existingCardMissedPayment.bind(this);
        this.getRedirectUrl = this.getRedirectUrl.bind(this);
        this.getReferrerJourney = this.getReferrerJourney.bind(this);
        this.obtainPaymentAmount = this.obtainPaymentAmount.bind(this);
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Payment processing");

        if (this.verificationNeeded()) {
            this.verifyNewCardMissedPayment(this.props.params.paymentReference, this.state.product);
        } else {
            this.existingCardMissedPayment(this.state.product);
        }

        this.customerPortalService.getPolicyPremiumTypeByPolicyNumber(this.props.params.policyNumber).subscribe((result: string) => {
            this.setState({
                premiumType: result,
                dataLoaded: true
            })
        });
    }

    public render() {
        const dataLoaded = this.state.dataLoaded;
        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                <FalconHeader/>
                {dataLoaded && this.renderPageContents()}
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderPageContents(): React.ReactNode {
        const {
            paymentSuccess,
            paymentCancelled,
            cardVerified
        } = this.state;

        if (cardVerified) {
            if (!paymentSuccess && !paymentCancelled) {
                this.logMessage("Payment processing");
                return <Navigate to={this.state.failureRedirectUrl}
                                 state={{verifyCardFailed: true}}/>
            } else {
                const redirectUrl: string = this.getRedirectUrl();
                if (paymentSuccess) {
                    const referrerJourney: string = this.getReferrerJourney();
                    window.sessionStorage.setItem("showPopup", "true");
                    window.sessionStorage.setItem("referrerJourney", referrerJourney);
                    window.sessionStorage.setItem("cardNumberObfuscated", this.state.cardNumberObfuscated);
                    this.cacheManager.clearCache("getPolicies");
                    this.obtainPaymentAmount();
                    if (this.state.paymentAmountReceived) {
                        return <Navigate to={redirectUrl}/>
                    }
                } else {
                    return <Navigate to={redirectUrl}/>
                }
            }
        }

        return (
            <div className="panel panel-processing">
                <h1 className="panel-heading">Processing...</h1>
                <h2 className="text-center h5">Once your {resourcesHelper.THEME} insurance subscription is set up, you will see a
                    payment confirmation screen and you will receive a confirmation email.</h2>
                <p>Please wait...</p>
                <p>Don't refresh or close your browser...</p>
                <img className="car" src="/img/Flourishes-5.svg"/>
            </div>
        )
    }

    private obtainPaymentAmount(): void {
        if (this.verificationNeeded()) {
            this.customerPortalService.getSettledAmountByPaymentReference(this.props.params.paymentReference).subscribe((amount: number) => {
                window.sessionStorage.setItem("paymentAmount", productHelper.isHomeOrWolfMotorByProduct(this.state.product) ?
                    getAmountToDisplay(amount) : getAmountToDisplay(Math.round(amount)));
                this.setState({paymentAmountReceived: true});
            });
        } else {
            this.setState({paymentAmountReceived: true});
        }
    }

    private getRedirectUrl():string {
        const premiumTypeBasedUrl: string = this.state.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            ? `/dashboard/monthlyPaymentHistory/${this.state.policyNumber ?? this.props.params.policyNumber}`
            : `/dashboard/annualPaymentHistory/${this.state.policyNumber ?? this.props.params.policyNumber}`;
        return productHelper.isHomeOrWolfMotorByProduct(this.state.product) && this.state.policyNumber
            ? premiumTypeBasedUrl
            : "/dashboard/paymentHistory";
    }

    private getReferrerJourney():string {
        const referrerJourneyByProduct:string = productHelper.isHomeOrWolfMotorByProduct(this.state.product)? "wolfMissedPayment" : "missedPayment";
        return this.state.isChargeBack ? "chargeBackPayment" : referrerJourneyByProduct;
    }

    private verifyNewCardMissedPayment(paymentReference: string, product: string): void {
        this.logMessage("Verify new payment card for missed payment");
        const policyNumber: string = this.state.policyNumber ?? this.props.params.policyNumber;
        if (policyNumber) {
            const request: MissedPayment = {
                paymentReferenceId: paymentReference,
                product,
                invoiceId: Number(this.props.params.invoiceId),
                isChargeback: this.state.isChargeBack,
                reuseCard: this.state.reuseCard
            };
            this.customerPortalService.verifyNewCardAndCompleteMissedOrOutstandingPaymentByPolicyNumber(policyNumber, request).subscribe(
                (response: VerifyNewCardResponse) => {
                    this.setState({
                        paymentSuccess: response && response.successful,
                        paymentCancelled: response && response.cancelled,
                        cardVerified: true,
                        failureRedirectUrl: policyNumber
                            ? `/dashboard/account/missedPolicyPayment/${policyNumber}`
                            : "/dashboard/account/missedPayment",
                        cardNumberObfuscated: response.paymentCard?.cardNumberObfuscated ?? "",
                    });
                });
        }
    }

    private existingCardMissedPayment(product: string): void {
        this.logMessage("Verify existing payment card for missed payment");
        const policyNumber: string = this.state.policyNumber ?? this.props.params.policyNumber;
        if (policyNumber) {
            if (this.state.isChargeBack) {
                this.customerPortalService.completeOutstandingPaymentWithMainPaymentCard(product, policyNumber).subscribe(
                    (response: FalconPaymentResponse) => {
                        this.setState({
                            paymentSuccess: response?.success,
                            paymentCancelled: false,
                            cardVerified: true,
                            failureRedirectUrl: `/dashboard/account/outstandingPayment/${policyNumber}`
                        });
                    });
            } else {
                if (this.props.params.invoiceId) {
                    const invoiceId: any = this.props.params.invoiceId;

                    this.customerPortalService.completeMissedPaymentWithMainPaymentCardForInvoice(product, policyNumber, invoiceId).subscribe(
                        (response: FalconPaymentResponse) => {
                            this.setState({
                                paymentSuccess: response?.success,
                                paymentCancelled: false,
                                cardVerified: true,
                                failureRedirectUrl: `/dashboard/account/payOverdueInstalment/${policyNumber}/${invoiceId}`
                            });
                        });
                } else {
                    this.customerPortalService.completeMissedPaymentWithMainPaymentCard(product, policyNumber).subscribe(
                        (response: FalconPaymentResponse) => {
                            this.setState({
                                paymentSuccess: response?.success,
                                paymentCancelled: false,
                                cardVerified: true,
                                failureRedirectUrl: `/dashboard/account/payOverdueInstalment/${policyNumber}`
                            });
                        });
                }
            }
        }
    }

    private verificationNeeded(): boolean {
        return Boolean(
            this.props.params.paymentReference &&
            this.props.params.paymentReference !== "null"
        );
    }

    private logMessage(message: string): void  {
        const policyNumber: string = this.state.policyNumber ?? this.props.params.policyNumber;
        this.apmRumLoggerService.log(message, [{
            "Policy number": policyNumber ?? "none"
        }]);
    }
}

export const PaymentProcessingPage = withRouter<{}, IParams>(PaymentProcessingPagePlain)
